/**
 * 공급계획(PSI) 날짜 및 주차(월~일 기준) 유틸리티
 * 
 * 1. 주는 월요일(Monday)에 시작하여 일요일(Sunday)에 종료됩니다.
 * 2. 동일한 주의 월요일부터 일요일까지는 모두 같은 주차로 계산됩니다.
 *    (예: 2026-10-19(월) ~ 2026-10-25(일) 모두 2026년 43주차)
 * 3. 기본 조회구간은 현재 일자의 주차 기준 과거 1주 ~ 미래 19주 (총 21주 구간)입니다.
 */

/**
 * 'YYYY-MM-DD' 문자열을 로컬 자정 기준 Date 객체로 파싱
 */
export function parseDate(str) {
    if (!str) return null;
    if (str instanceof Date) {
        return isNaN(str.getTime()) ? null : new Date(str.getFullYear(), str.getMonth(), str.getDate());
    }
    const parts = String(str).trim().split("-");
    if (parts.length !== 3) return null;
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    const date = new Date(y, m, d);
    return isNaN(date.getTime()) ? null : date;
}

/**
 * Date 객체를 'YYYY-MM-DD' 문자열로 포맷
 */
export function formatDate(date) {
    if (!date) return "";
    const d = parseDate(date);
    if (!d) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

/**
 * 날짜의 ISO 8601 기준 연도와 주차(월~일 기준, 1~53주) 계산
 * - 월요일~일요일은 모두 같은 주차로 계산됩니다.
 * @param {string|Date} dateInput
 * @returns {{ year: number, week: number }}
 */
export function getWeekFromDate(dateInput) {
    const d = parseDate(dateInput);
    if (!d) return null;

    // UTC 기준으로 변환하여 일광절약시간/시간대 오차 제거
    const utcDate = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    // 일요일은 getUTCDay()가 0이므로 7로 처리 (월:1 ~ 일:7)
    const dayNum = utcDate.getUTCDay() || 7;

    // 해당 주의 목요일로 이동 (목요일이 속한 연도가 해당 주차의 연도)
    utcDate.setUTCDate(utcDate.getUTCDate() + 4 - dayNum);

    const year = utcDate.getUTCFullYear();
    const yearStart = new Date(Date.UTC(year, 0, 1));
    const weekNo = Math.ceil((((utcDate - yearStart) / 86400000) + 1) / 7);

    return {
        year,
        week: weekNo,
    };
}

/**
 * 특정 날짜가 속한 주의 월요일과 일요일 Date 구하기
 * @param {string|Date} dateInput
 * @returns {{ monday: Date, sunday: Date }}
 */
export function getWeekRangeFromDate(dateInput) {
    const d = parseDate(dateInput);
    if (!d) return { monday: null, sunday: null };

    const day = d.getDay(); // 0: 일요일, 1: 월요일, ..., 6: 토요일
    // 월요일까지의 날짜 차이 (일요일이면 -6일, 그 외는 1 - day)
    const diffToMonday = day === 0 ? -6 : 1 - day;

    const monday = new Date(d);
    monday.setDate(d.getDate() + diffToMonday);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    return { monday, sunday };
}

/**
 * 특정 연도와 주차 구간(1~53주)으로부터 시작일자(YYYY-MM-DD)와 종료일자(YYYY-MM-DD) 계산
 * - 1주차 시작: 해당 연도 1월 1일 (또는 월요일)
 * - 53주차 종료: 해당 연도 12월 31일 (연말 범위 초과 방지)
 * - 중간 주차: 해당 주차의 월요일 ~ 일요일
 * @param {number} year
 * @param {number} startWeek
 * @param {number} endWeek
 * @returns {{ startDate: string, endDate: string }}
 */
export function getDateRangeFromWeeks(year, startWeek, endWeek) {
    const y = parseInt(year, 10) || new Date().getFullYear();
    const sw = Math.max(1, Math.min(53, parseInt(startWeek, 10) || 1));
    const ew = Math.max(sw, Math.min(53, parseInt(endWeek, 10) || 53));

    // ISO 8601: 1월 4일이 속한 주의 월요일이 1주차 월요일
    const jan4 = new Date(y, 0, 4);
    const day = jan4.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const week1Monday = new Date(y, 0, 4 + diffToMonday);

    let startDate;
    if (sw === 1) {
        startDate = `${y}-01-01`;
    } else {
        const m = new Date(week1Monday);
        m.setDate(week1Monday.getDate() + (sw - 1) * 7);
        startDate = formatDate(m);
    }

    let endDate;
    if (ew >= 53) {
        endDate = `${y}-12-31`;
    } else {
        const s = new Date(week1Monday);
        s.setDate(week1Monday.getDate() + (ew - 1) * 7 + 6);
        endDate = formatDate(s);
    }

    return { startDate, endDate };
}

/**
 * 기본 조회구간 계산 (현재 일자 주차 기준: 과거 1주 ~ 미래 19주, 총 21주 구간)
 * 예: 이번 주가 21주차면 -> 20주차(과거 1주) ~ 40주차(미래 19주)
 * @param {Date} [baseDate=new Date()]
 * @returns {{
 *   startDate: string,
 *   endDate: string,
 *   startYear: number,
 *   startWeek: number,
 *   endYear: number,
 *   endWeek: number,
 *   totalWeeks: number,
 *   label: string,
 *   isCustom: boolean
 * }}
 */
export function getDefault20WeekRange(baseDate = new Date()) {
    const today = parseDate(baseDate) || new Date();
    const { monday: thisMonday, sunday: thisSunday } = getWeekRangeFromDate(today);

    // 과거 1주 전 주의 월요일
    const startDate = new Date(thisMonday);
    startDate.setDate(thisMonday.getDate() - 7);

    // 미래 19주 후 주의 일요일
    const endDate = new Date(thisSunday);
    endDate.setDate(thisSunday.getDate() + (19 * 7));

    const sWeek = getWeekFromDate(startDate);
    const eWeek = getWeekFromDate(endDate);

    const sYrStr = sWeek ? `${sWeek.year}년 ` : "";
    const eYrStr = eWeek && sWeek && sWeek.year !== eWeek.year ? `${eWeek.year}년 ` : "";

    return {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        startYear: sWeek ? sWeek.year : today.getFullYear(),
        startWeek: sWeek ? sWeek.week : 1,
        endYear: eWeek ? eWeek.year : today.getFullYear(),
        endWeek: eWeek ? eWeek.week : 53,
        totalWeeks: 21,
        label: `${sYrStr}${sWeek?.week}주 ~ ${eYrStr}${eWeek?.week}주 (총 21주)`,
        isCustom: false,
    };
}

/**
 * 시작일자와 종료일자로부터 주차 구간 정보 계산
 */
export function getWeekRangeInfo(startDateStr, endDateStr) {
    const sDate = parseDate(startDateStr);
    const eDate = parseDate(endDateStr);
    if (!sDate || !eDate) return null;

    const sWeek = getWeekFromDate(sDate);
    const eWeek = getWeekFromDate(eDate);
    if (!sWeek || !eWeek) return null;

    // 주 수 계산 (시작 월요일부터 종료 일요일까지 주 단위 카운트)
    const { monday: startMonday } = getWeekRangeFromDate(sDate);
    const { sunday: endSunday } = getWeekRangeFromDate(eDate);
    const diffDays = Math.round((endSunday - startMonday) / 86400000) + 1;
    const totalWeeks = Math.max(1, Math.round(diffDays / 7));

    const isSameYear = sWeek.year === eWeek.year;
    const label = isSameYear
        ? `${sWeek.year}년 ${sWeek.week}주 ~ ${eWeek.week}주 (${totalWeeks}주)`
        : `${sWeek.year}년 ${sWeek.week}주 ~ ${eWeek.year}년 ${eWeek.week}주 (${totalWeeks}주)`;

    return {
        startDate: formatDate(sDate),
        endDate: formatDate(eDate),
        startYear: sWeek.year,
        startWeek: sWeek.week,
        endYear: eWeek.year,
        endWeek: eWeek.week,
        totalWeeks,
        label,
    };
}

/**
 * 날짜 구간에 포함되는 연속된 activeWeeks와 activeMonths 생성
 * - 월요일~일요일 단위로 7일씩 전진하며 정확한 연도/주차/월 매핑을 생성합니다.
 * - 2년에 걸쳐 있더라도 정확하게 연속 처리됩니다.
 */
export function generateActiveWeeksAndMonths(startDateStr, endDateStr, baseWeekCode = "", currentYearNum = new Date().getFullYear()) {
    const sDate = parseDate(startDateStr);
    const eDate = parseDate(endDateStr);
    if (!sDate || !eDate || sDate > eDate) {
        return { activeWeeks: [], activeMonths: [] };
    }

    // 기준주차에서 기준 주 번호 추정 (예: 20261043_2 -> 43주)
    let baseWeekNum = 36;
    if (baseWeekCode) {
        const m = String(baseWeekCode).match(/(\d{2})_/) || String(baseWeekCode).match(/(\d{4})(\d{2})/);
        if (m && m[1]) {
            const num = parseInt(m[1], 10);
            if (!isNaN(num) && num >= 1 && num <= 53) {
                baseWeekNum = num;
            }
        }
    }

    const { monday: startMonday } = getWeekRangeFromDate(sDate);
    const { sunday: endSunday } = getWeekRangeFromDate(eDate);

    const activeWeeks = [];
    const monthsMap = new Map(); // key: "YYYY_M" -> { key, year, month, label, weeks: [] }

    let curr = new Date(startMonday);
    // 종료 주의 일요일까지 7일씩 전진
    while (curr <= endSunday) {
        const weekInfo = getWeekFromDate(curr);
        if (!weekInfo) break;
        const yr = weekInfo.year;
        const w = weekInfo.week;
        const yr2 = String(yr).slice(-2);

        // 해당 주의 목요일 날짜가 속한 월을 대표 월로 지정 (ISO 표준)
        const thurs = new Date(curr);
        thurs.setDate(curr.getDate() + 3);
        const m = thurs.getMonth() + 1;

        // 실적(Actual) vs 계획(Plan) 판단:
        // 과거 년도는 100% 실적, 미래 년도는 100% 계획, 당해 연도는 baseWeekNum 이전이 실적
        let isActual = false;
        if (yr < currentYearNum) {
            isActual = true;
        } else if (yr > currentYearNum) {
            isActual = false;
        } else {
            isActual = w < baseWeekNum;
        }

        const wKey = `${yr}_${w}`;
        const mKey = `${yr}_${m}`;

        const wItem = {
            key: wKey,
            year: yr,
            week: w,
            label: `${yr2}-${String(w).padStart(2, "0")}`,
            month: m,
            monthKey: mKey,
            isActual,
        };
        activeWeeks.push(wItem);

        if (!monthsMap.has(mKey)) {
            monthsMap.set(mKey, {
                key: mKey,
                year: yr,
                month: m,
                label: `${yr2}년 ${m}월`,
                weeks: [],
            });
        }
        monthsMap.get(mKey).weeks.push(wItem);

        // 다음 주 월요일로 7일 이동
        curr.setDate(curr.getDate() + 7);
    }

    const activeMonths = Array.from(monthsMap.values());
    const distinctYears = new Set(activeWeeks.map(w => w.year));
    // 단일 연도인 경우 월 라벨을 "10월" 형태로 간결화
    if (distinctYears.size <= 1) {
        activeMonths.forEach(m => {
            m.label = `${m.month}월`;
        });
    }

    return { activeWeeks, activeMonths };
}

/**
 * 빠른선택 프리셋 날짜 구간 목록 반환
 */
export function getPresetDateRanges(baseDate = new Date()) {
    const today = parseDate(baseDate) || new Date();
    const currYear = today.getFullYear();

    // 1. 기본 (과거 1주 ~ 미래 19주)
    const def = getDefault20WeekRange(today);

    // 2. 전체 (당해 연도 1월 1일 ~ 12월 31일)
    const fullYear = {
        startDate: `${currYear}-01-01`,
        endDate: `${currYear}-12-31`,
    };

    // 3. 상반기 (당해 연도 1월 1일 ~ 6월 30일)
    const h1 = {
        startDate: `${currYear}-01-01`,
        endDate: `${currYear}-06-30`,
    };

    // 4. 하반기 (당해 연도 7월 1일 ~ 12월 31일)
    const h2 = {
        startDate: `${currYear}-07-01`,
        endDate: `${currYear}-12-31`,
    };

    return {
        default: def,
        fullYear,
        h1,
        h2,
    };
}
